import React, { FC, useEffect, useMemo, useState } from "react";
import { ColDef } from "ag-grid-community";
import classNames from "classnames";
import { ColsData } from "features/BaseDocumentsLayout/Body";
import { Header } from "features/Reports/Header";
import { useAtom } from "jotai/index";

import { Table } from "components/Table/Table";
import { RowRender } from "components/RowRender/RowRender";
import { HorizontalSpace } from "components/HorizontalSpace/HorizontalSpace";
import { Button } from "components/Button/Button";
import { BaseLayout } from "components/BaseLayout/BaseLayout";
import { SearchInput } from "components/SearchInput/SearchInput";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";

import {
  reportsAtom,
  reportsFilteredAtom,
  reportsHeadersAtom,
} from "../../atoms/reportsAtom";
import useWindowSizes from "../../hooks/useWindowSizes";

import styles from "./styles.module.css";

interface ReportTable {
  title: string;
  type: "profit" | "movement";
}

const getBottomRow = (reports: any[] = [], type: "profit" | "movement") => {
  const keys = Object.keys(reports[0]);
  const finalObj: any = {};

  keys.forEach((key) => {
    if (!["number", "groupname", "itemname"].includes(key)) {
      finalObj[key] = reports.reduce((acc, i) => acc + +i[key], 0);
    }
  });

  if (type === "profit") {
    finalObj.salepercent =
      +finalObj.cost123 !== 0
        ? (
            ((+finalObj.total12 - +finalObj.cost123) / +finalObj.cost123) *
            100
          ).toFixed(2)
        : "0.00";
    finalObj.rentability =
      +finalObj.total12 !== 0
        ? (
            ((+finalObj.total12 - +finalObj.cost123) / +finalObj.total12) *
            100
          ).toFixed(2)
        : "0.00";
  }

  return finalObj;
};

export const ReportTable: FC<ReportTable> = ({ title, type }): JSX.Element => {
  const { height } = useWindowSizes();
  const [reports, setReports] = useAtom(reportsAtom);
  const [reportsFiltered, setReportsFiltered] = useAtom(reportsFilteredAtom);
  const [reportsHeaders, setReportsHeaders] = useAtom(reportsHeadersAtom);
  const [searchText, setSearchText] = useState("");

  const colDefs: ColDef<ColsData>[] = useMemo(
    () =>
      reportsHeaders.map((i) => ({
        ...i,
        resizable: Boolean(i.resizable),
        cellRenderer: RowRender,
      })),
    [reportsHeaders]
  );

  useEffect(() => {
    if (!searchText.length) {
      return setReportsFiltered(reports);
    }

    const filtered = reports.filter((item) =>
      JSON.stringify(item).toLowerCase().includes(searchText.toLowerCase())
    );
    setReportsFiltered(filtered);
  }, [searchText, reports]);

  return (
    <BaseLayout
      className={classNames("ml-2 mt-3 mb-3 px-8" as any)}
      header={
        <>
          <Header title={title} />
          <VerticalSpace size={10} />
          <SearchInput callback={setSearchText} />
        </>
      }
      footer={<div />}
    >
      <VerticalSpace size={20} />
      <div className={styles.container}>
        <Table
          height={height * 0.8}
          rowData={reportsFiltered}
          colDefs={colDefs as any}
          pinnedBottomRowData={
            reports?.length ? [getBottomRow(reports, type)] : undefined
          }
        />
      </div>
    </BaseLayout>
  );
};
